import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import axios from 'axios';
import { useListRestore, useScrollRestore } from '@/common/listRestore';
import RecruitmentGrid from '@/components/domain/recruitment/RecruitmentGrid';
import { RecruitmentListData } from '@/components/domain/recruitment/types';
import NoData from '@/components/common/elements/NoData';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import PageState from '@/components/common/PageState';

const Container = styled.div`
    padding: 2rem 0;

    @media (max-width: 768px) {
        padding: 1rem 0;
    }
`;

const Title = styled.h1`
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
        font-size: 1.25rem;
        margin-bottom: 1rem;
    }
`;

const WelfareRecruitmentList = () => {
    const [searchParams] = useSearchParams();
    const welfareType = searchParams.get('welfareType') || '';

    // 공고를 보고 뒤로 왔을 때: 같은 복지 유형이면 다시 받지 않고 보던 목록·스크롤 위치를 되살린다
    const { restored: restoredRaw, save } = useListRestore<{ welfareType: string; recruitments: RecruitmentListData[]; total: number; title: string }>('recruitments:welfare');
    const restored = restoredRaw && restoredRaw.welfareType === welfareType ? restoredRaw : null;
    useScrollRestore(restored !== null);
    const restoredTypeRef = useRef<string | null>(restored ? welfareType : null);
    const [recruitments, setRecruitments] = useState<RecruitmentListData[]>(restored?.recruitments ?? []);
    const [total, setTotal] = useState(restored?.total ?? 0);
    const [title, setTitle] = useState(restored?.title ?? '복지 관련 채용공고');
    // welfareType 이 없으면 아무것도 받지 않으므로 처음부터 로딩이 아니다 (예전엔 스피너가 영원히 돌았다)
    const [loading, setLoading] = useState(restored === null && welfareType !== '');
    /** 직전 조회가 실패했는지 — 실패를 "검색 결과 없음" 으로 보여주면 공고가 없는 줄 오해한다 */
    const [hasError, setHasError] = useState(false);
    /** "다시 시도" 로 같은 복지 유형을 다시 받게 하는 신호 */
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (!welfareType) return undefined;
        if (restoredTypeRef.current === welfareType) return undefined;
        restoredTypeRef.current = null;

        // 화면을 떠났거나 복지 유형이 바뀐 뒤 도착한 응답은 버린다 (엉뚱한 목록·에러가 덮어쓰는 것 방지)
        let ignore: boolean = false;
        setLoading(true);
        axios.get<{ result: { list?: RecruitmentListData[]; total?: number; title?: string } }>(`/api/recruitments/welfare`, {
            params: {
                welfareType,
                limit: 30,
                offset: 0
            }
        })
        .then((response) => {
            if (ignore) return;
            const result = response.data.result;
            setRecruitments(result.list || []);
            setTotal(result.total || 0);
            setTitle(result.title || '복지 관련 채용공고');
            setHasError(false);
            save({ welfareType, recruitments: result.list || [], total: result.total || 0, title: result.title || '복지 관련 채용공고' });
        })
        .catch((error) => {
            if (ignore) return;
            console.error('Failed to fetch welfare recruitments:', error);
            // 실패한 목록은 저장하지 않는다 — 되돌아왔을 때 빈 목록이 되살아나면 안 된다
            setHasError(true);
        })
        .finally(() => {
            if (ignore) return;
            setLoading(false);
        });

        return () => {
            ignore = true;
        };
    }, [welfareType, retryCount]);

    // 복지 유형 없이 들어온 경우 — 받을 게 없으니 길을 안내한다 (예전엔 스피너만 계속 돌았다)
    if (!welfareType) {
        return (
            <Container>
                <PageState
                    title="복지 유형이 지정되지 않았습니다"
                    description="보고 싶은 복지 조건을 채용공고 목록에서 선택해 주세요."
                    action={{ label: '채용공고 보러 가기', to: '/recruitments/region' }}
                />
            </Container>
        );
    }

    if (loading) {
        return <LoadingSpinner placement="page" />;
    }

    return (
        <Container>
            <Title>{title}</Title>
            {hasError ? (
                <PageState
                    title="채용공고를 불러오지 못했습니다"
                    description="네트워크 상태를 확인한 뒤 다시 시도해 주세요."
                    error
                    onRetry={() => setRetryCount((count: number) => count + 1)}
                />
            ) : total > 0 ? (
                <RecruitmentGrid data={recruitments} layout="list" />
            ) : (
                <NoData icon={null} message="검색 결과가 없습니다." />
            )}
        </Container>
    );
};

export default WelfareRecruitmentList;
